import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import CollapsibleSidebar from './CollapsibleSidebar';

const DashboardSidebar = ({ 
  currentPage = 'dashboard', 
  children, 
  onOpenV12Diagnostic,
  onOpenDailyDecision,
  dailyTasks = { dailyDecision: false, v12Diagnostic: false },
  weeklyActions = [], // Array of { id, title, type, schedule, completed, onPress }
}) => {
  const { navigateTo } = useNavigation();
  const [isTasksCollapsed, setIsTasksCollapsed] = React.useState(false);

  const menuItems = [
    { id: 'dashboard', label: 'My Dashboard', screen: 'MyDashboard' },
    { id: 'courses', label: 'Courses', screen: 'Courses' },
    { id: 'calendar', label: 'My Calendar', screen: 'Calendar' },
    { id: 'team', label: 'My Team', screen: 'TigerTeam' },
    { id: 'glossary', label: 'Glossary', screen: 'Glossary' },
  ];

  // Get icon for weekly action type
  const getWeeklyActionIcon = (type) => {
    switch (type) {
      case 'strategic_decision':
        return 'bullseye-arrow';
      case 'strategic_action':
        return 'lightning-bolt';
      case 'tiger_team_breakouts':
        return 'account-group';
      default:
        return 'chart-box-outline';
    }
  };

  // Get schedule display text
  const getScheduleText = (schedule) => {
    if (!schedule || !schedule.type) return '';
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    switch (schedule.type) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        if (schedule.dayOfWeek !== undefined) {
          return dayNames[schedule.dayOfWeek];
        }
        return 'Weekly';
      case 'monthly':
        if (schedule.dayOfMonth !== undefined) {
          return `Day ${schedule.dayOfMonth}`;
        }
        return 'Monthly';
      default:
        return schedule.type;
    }
  };

  // Build tasks list - always show both items, with completed status
  const allTasks = [
    { 
      id: 'dailyDecision', 
      label: 'Daily Decision', 
      icon: 'head-question-outline',
      onPress: onOpenDailyDecision,
      completed: dailyTasks.dailyDecision,
      scheduleText: 'Daily',
    },
    { 
      id: 'v12', 
      label: 'V12 Diagnostic', 
      icon: 'engine-outline',
      onPress: onOpenV12Diagnostic,
      isV12: true,
      completed: dailyTasks.v12Diagnostic,
      scheduleText: 'Monthly',
    },
    // Add weekly actions
    ...weeklyActions.map(action => ({
      id: action.id,
      label: action.title,
      icon: getWeeklyActionIcon(action.type),
      onPress: action.onPress,
      completed: action.completed,
      scheduleText: getScheduleText(action.schedule),
      isWeeklyAction: true,
    }))
  ];
  
  const pendingCount = allTasks.filter(t => !t.completed).length;

  return (
    <CollapsibleSidebar itemCount={menuItems.length}>
      <View style={styles.sidebarList}>
        {menuItems.map((item) => {
          const isActive = currentPage === item.id;
            
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.sidebarItem,
                isActive && styles.activeItem,
              ]}
              onPress={() => {
                if (item.screen) {
                  navigateTo(item.screen);
                }
              }}
            >
              <Text
                style={[
                  styles.sidebarText,
                  isActive && styles.sidebarTextActive,
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        
        {/* My Tasks Section - Collapsible */}
        <View style={styles.dailyTasksSection}>
          <TouchableOpacity 
            style={styles.dailyTasksHeader}
            onPress={() => setIsTasksCollapsed(!isTasksCollapsed)}
          >
            <MaterialCommunityIcons 
              name={isTasksCollapsed ? "chevron-right" : "chevron-down"} 
              size={16} 
              color="white" 
            />
            <Text style={styles.dailyTasksTitle}>My Tasks</Text>
            {pendingCount > 0 && (
              <View style={styles.taskCountBadge}>
                <Text style={styles.taskCountText}>{pendingCount}</Text>
              </View>
            )}
            {pendingCount === 0 && (
              <MaterialCommunityIcons name="check-circle" size={16} color="#4ADE80" />
            )}
          </TouchableOpacity>
          
          {!isTasksCollapsed && allTasks.map((task) => (
            <TouchableOpacity
              key={task.id}
              style={[
                styles.taskItem,
                task.completed && styles.taskItemCompleted,
              ]}
              onPress={task.onPress}
            >
              <MaterialCommunityIcons 
                name={task.completed ? "checkbox-marked-circle" : task.icon || "checkbox-blank-circle-outline"} 
                size={14} 
                color={task.completed ? "#4ADE80" : "white"} 
              />
              <View style={styles.taskLabelContainer}>
                <Text style={[
                  styles.taskLabel, 
                  task.isV12 && !task.completed && styles.taskLabelV12,
                  task.completed && styles.taskLabelCompleted
                ]}>
                  {task.label}
                </Text>
                {task.scheduleText && (
                  <Text style={[
                    styles.taskScheduleText,
                    task.completed && styles.taskScheduleTextCompleted
                  ]}>
                    {task.scheduleText}
                  </Text>
                )}
              </View>
              <MaterialCommunityIcons 
                name={task.completed ? "eye-outline" : "chevron-right"} 
                size={14} 
                color={task.completed ? "rgba(255,255,255,0.5)" : "white"} 
              />
            </TouchableOpacity>
          ))}
        </View>
        
        {/* Render custom children content below */}
        {children}
      </View>
    </CollapsibleSidebar>
  );
};

const styles = StyleSheet.create({
  sidebarList: {
    gap: 8,
    paddingHorizontal: 16,
  },
  sidebarItem: {
    alignSelf: 'stretch',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    minHeight: 40,
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  activeItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  sidebarText: {
    flex: 1,
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '500',
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  sidebarTextActive: {
    fontWeight: '700',
    color: '#000000',
    ...Platform.select({
      web: {
        textShadow: 'none',
      },
    }),
  },
  // My Tasks Section
  dailyTasksSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.15)',
  },
  dailyTasksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  dailyTasksTitle: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '600',
    color: 'white',
    flex: 1,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  taskCountBadge: {
    backgroundColor: 'white',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  taskCountText: {
    fontSize: 11,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    color: '#FF4747',
  },
  taskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(0,0,0,0.15)',
    borderRadius: 8,
    marginBottom: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  taskLabelContainer: {
    flex: 1,
  },
  taskLabel: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '500',
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  taskScheduleText: {
    fontSize: 10,
    fontFamily: 'Public Sans',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.6)',
    marginTop: 2,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  taskScheduleTextCompleted: {
    color: 'rgba(255,255,255,0.4)',
  },
  taskLabelV12: {
    color: '#F5E6A3',
  },
  taskItemCompleted: {
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
  },
  taskLabelCompleted: {
    color: 'rgba(255,255,255,0.7)',
    textDecorationLine: 'line-through',
  },
});

export default DashboardSidebar;
